import { useCallback, useMemo, type ReactNode } from 'react'

import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { useGetNoticesQuery, useUpdateNoticeMutation } from '../../../store/notices/notices'
import { MCP_PROMO_BLOCKS, McpPromoPage, type McpPromoBlockConfig, type McpPromoVariant } from './mcpPromoConfig'
import { useMcpPromoEligibility } from './useMcpPromoEligibility'

export interface UseMcpPromoBannerResult {
  variant: McpPromoVariant | null
  block: McpPromoBlockConfig | null
  shouldShow: boolean
  isFetching: boolean
  dismiss: () => void
  probes: ReactNode
}

export const useMcpPromoBanner = (page: McpPromoPage): UseMcpPromoBannerResult => {
  const accountId = useAppSelector(selectAccountId)

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const blocks = MCP_PROMO_BLOCKS[page]

  const dismissedKeys = useMemo(() => new Set((noticesData?.data ?? []).filter((notice) => notice.isDismissed).map((notice) => notice.key)), [noticesData])

  const allDismissed = Object.values(blocks).every((block) => block !== undefined && dismissedKeys.has(block.noticeKey))

  const {
    mcpAppsAvailable,
    mcpAppsEnabled,
    hasMcpConnection,
    canManageOAuth2Apps,
    oauth2Enabled,
    isFetching: isEligibilityFetching,
    probes,
  } = useMcpPromoEligibility({ skip: allDismissed })

  const variant: McpPromoVariant | null = useMemo(() => {
    if (page === McpPromoPage.INTEGRATION_SETTINGS) {
      const canEnableMcpApps = canManageOAuth2Apps && oauth2Enabled && mcpAppsAvailable && !mcpAppsEnabled
      return canEnableMcpApps ? 'admin' : null
    }
    if (!mcpAppsEnabled) {
      return null
    }
    return hasMcpConnection ? 'interest' : 'connect'
  }, [page, canManageOAuth2Apps, oauth2Enabled, mcpAppsAvailable, mcpAppsEnabled, hasMcpConnection])

  const block = (variant ? blocks[variant] : undefined) ?? null
  const isDismissed = block ? dismissedKeys.has(block.noticeKey) : false

  const isFetching = isNoticesFetching || isDismissing || isEligibilityFetching

  const shouldShow = block !== null && !isDismissed && !isFetching

  const dismiss = useCallback(() => {
    if (!block) {
      return
    }
    updateNotice({ accountId, key: block.noticeKey, isDismissed: true })
  }, [accountId, block, updateNotice])

  return {
    variant,
    block,
    shouldShow,
    isFetching,
    dismiss,
    probes,
  }
}
